import { createAsyncThunk } from "@reduxjs/toolkit"

import { AmplitudeEvent } from "@common-shared/config/amplitude.config.ts"
import { IPolicyIdentificationData } from "@common-shared/types/policies.types.ts"
import type { IPractice } from "@common-shared/types/responseData/commonResponseData.types.ts"

import { ToastrHelper } from "@front-shared/helpers/toastr.helper.tsx"

import { OVERRIDES_CONFIG } from "../../config/overrides.config"
import { AmplitudeHelper } from "../../helpers/amplitude.helper"
import { UtilHelper } from "../../helpers/util.helper"
import { CoverageOverridesService } from "../../services/coverageOverrides.service"
import type {
    ICoverageOverrideResult,
    IOverrideUpsertRequestRawData,
} from "../../types/data.types.ts"
import type { IThunkActionWithPracticeData } from "../../types/general.types.ts"
import { coverageOverridesSetSelectedPolicyOverride } from "../slices/coverageOverrides.slice.ts"
import {
    runtimeStartLoading,
    runtimeStopLoading,
} from "../slices/runtime.slice"

const upsertSingleOverride = async (
    practice: IPractice,
    data: IOverrideUpsertRequestRawData,
): Promise<ICoverageOverrideResult> => {
    try {
        const result = await CoverageOverridesService.upsert(practice, data)

        if (!result) {
            throw new Error(
                `Failed to create override for member ID: ${data.policyIdentification.memberId}`,
            )
        }

        // Log Amplitude event when override(s) is created or updated
        if (result.success.length > 0) {
            const override = result.success[0]

            const isNewOverride = override.createdAt === override.updatedAt

            AmplitudeHelper.logEvent(
                isNewOverride
                    ? AmplitudeEvent.CREATED_OVERRIDE
                    : AmplitudeEvent.UPDATED_OVERRIDE,
                {
                    practiceId: practice.id,
                    practiceName: practice.name,
                },
            )
        }

        return result
    } catch (e: any) {
        return {
            success: [],
            errors: [
                {
                    msg: e.message,
                    requestData:
                        CoverageOverridesService.getMappedOverrideData(data),
                },
            ],
        }
    }
}

/**
 * Create coverage overrides in bulk
 */
export const createCoverageOverrides = createAsyncThunk<
    void,
    IThunkActionWithPracticeData<{
        data: IOverrideUpsertRequestRawData[]
        sleepAtTheEndSeconds?: number
    }>
>("createCoverageOverridesPrefix", async (action, { dispatch }) => {
    if (!action.ignorePreloader) {
        dispatch(runtimeStartLoading("createCoverageOverridesLoading"))
    }

    try {
        const batchSize = OVERRIDES_CONFIG.bulkOverrides.creationChunkSize
        const finalResult: ICoverageOverrideResult = {
            success: [],
            errors: [],
        }

        let processedCount = 0

        while (processedCount < action.payload.data.length) {
            const overrideResults = [] as Promise<ICoverageOverrideResult>[]

            // Generate processing promises with our "pagination' based on batch size
            for (let i = processedCount; i < processedCount + batchSize; i++) {
                const payload = action.payload.data[i]

                if (!payload) {
                    continue
                }

                overrideResults.push(
                    upsertSingleOverride(action.practice, payload),
                )
            }

            processedCount += batchSize

            const results = await Promise.all(overrideResults)

            // Add new results to final results
            for (const result of results) {
                finalResult.success = [
                    ...finalResult.success,
                    ...result.success,
                ]

                finalResult.errors = [...finalResult.errors, ...result.errors]
            }
        }

        // history service is not being updated right away with new data - there's some delay
        // make sure to wait until getting new data
        await UtilHelper.sleep(action.payload.sleepAtTheEndSeconds || 2)

        action.onSuccess?.(finalResult)
    } catch (e) {
    } finally {
        dispatch(runtimeStopLoading("createCoverageOverridesLoading"))
    }
})

/**
 * Get single override
 */
export const getSingleCoverageOverride = createAsyncThunk<
    void,
    IThunkActionWithPracticeData<{
        data: IPolicyIdentificationData
    }>
>("getSingleCoverageOverridePrefix", async (action, { dispatch }) => {
    if (!action.ignorePreloader) {
        dispatch(runtimeStartLoading("getSingleCoverageOverrideLoading"))
    }

    // reset selected override to null before loading new override, so we don't show override data for previous policy while loading new
    dispatch(coverageOverridesSetSelectedPolicyOverride(null))

    try {
        const result = await CoverageOverridesService.getSingle(
            action.practice,
            action.payload.data,
        )

        if (result) {
            dispatch(coverageOverridesSetSelectedPolicyOverride(result))

            action.onSuccess?.(result)
        } else {
            action.onError?.(result)
        }
    } catch (e) {
    } finally {
        dispatch(runtimeStopLoading("getSingleCoverageOverrideLoading"))
    }
})

/**
 * Delete single override
 */
export const deleteSingleCoverageOverride = createAsyncThunk<
    void,
    IThunkActionWithPracticeData<{
        overrideNirvanaRequestId: string
    }>
>("deleteSingleCoverageOverridePrefix", async (action, { dispatch }) => {
    if (!action.ignorePreloader) {
        dispatch(runtimeStartLoading("deleteSingleCoverageOverrideLoading"))
    }

    try {
        const result = await CoverageOverridesService.deleteSingle(
            action.practice,
            action.payload.overrideNirvanaRequestId,
        )

        // Log Amplitude event when override(s) is deleted
        AmplitudeHelper.logEvent(AmplitudeEvent.DELETED_OVERRIDE, {
            practiceId: action.practice.id,
            practiceName: action.practice.name,
        })

        if (result?.nirvanaRequestId) {
            // history service is not being updated right away with new data - there's some delay
            // make sure to wait until getting new data
            await UtilHelper.sleep(2)

            action.onSuccess?.(result.nirvanaRequestId)
        } else {
            ToastrHelper.error(
                "Failed to remove override. Please try again or contact support",
            )

            action.onError?.(result)
        }
    } catch (e) {
    } finally {
        dispatch(runtimeStopLoading("deleteSingleCoverageOverrideLoading"))
    }
})
